// src/services/candidateLinks.ts
//
// The one page a candidate opens from a link in a message. Newer clients exchange
// that URL code for a short HttpOnly cookie before any document write; older,
// feature-flagged-off clients retain their original bearer-link behavior.
import { api } from './api';
import { captchaToken } from './candidateCaptcha';

export interface SentFile {
  uuid: string;
  name: string;
  sent_at: string;
  /** Which requested document this file was sent for, when there was a choice. */
  key?: string | null;
}

/** One thing this application asks for, in the words the client chose. */
export interface RequestedDocument {
  key: string;
  label: string;
}

/** Retained only to let an unexpired split link finish its original purpose. */
export interface ChecklistQuestion {
  id: number;
  question: string;
  options?: string[];
}

export interface Checklist {
  complete: boolean;
  questions: ChecklistQuestion[];
}

export interface ChecklistSubmitResult {
  saved: number;
  complete: boolean;
  questions: ChecklistQuestion[];
}

/** Retained only for certification links issued before the unified rollout. */
export interface Certifications {
  consent_given: boolean;
  sent: SentFile[];
}

export interface CandidateLink {
  /** Where the blank checklist lives, when this client provides one. Absent otherwise. */
  template_url?: string | null;
  consent_given: boolean;
  sent: SentFile[];
  /** Empty when the client asks for one thing, so there is nothing to choose. */
  requested?: RequestedDocument[];
  /** The candidate's language, when the API provides it. */
  language?: string;
}

export type ReissueStatus = 'sent' | 'limit_reached';

export interface RedeemedLinkSession {
  csrf_token: string;
  session_id: string;
  template_url?: string | null;
  requested?: RequestedDocument[];
  /** The candidate's language, when the API provides it. */
  language?: string;
  cookie_session: true;
}

export interface CookieSessionDisabled {
  cookie_session: false;
}

const base = (code: string) => `/candidate-links/${encodeURIComponent(code)}`;

// Every write here is captcha gated on the API. The consent write sends a JSON
// body and cannot carry the token in a form field, so both carry it in a header.
const guarded = async (action: string) => ({
  headers: { 'Recaptcha-Token': await captchaToken(action) },
});

const sessionBase = (session: RedeemedLinkSession) =>
  `/candidate-links/session/${encodeURIComponent(session.session_id)}`;

const sessionGuarded = async (session: RedeemedLinkSession, action: string) => ({
  withCredentials: true,
  headers: {
    'Recaptcha-Token': await captchaToken(action),
    'X-CSRFToken': session.csrf_token,
  },
});

/** Exchange a URL code once; all later writes use its HttpOnly cookie instead. */
export async function redeemLink(
  code: string,
): Promise<RedeemedLinkSession | CookieSessionDisabled> {
  const { data } = await api.post<RedeemedLinkSession | CookieSessionDisabled>(
    '/candidate-links/redeem',
    { code },
    { withCredentials: true },
  );
  return data;
}

export async function acceptSessionConsent(
  session: RedeemedLinkSession,
  version: string,
): Promise<void> {
  await api.post(
    `${sessionBase(session)}/consent`,
    { version },
    await sessionGuarded(session, 'candidate_documents'),
  );
}

export async function uploadSessionFiles(
  session: RedeemedLinkSession,
  files: File[],
  key?: string,
): Promise<void> {
  const form = new FormData();
  files.forEach((file) => {
    form.append('files', file);
    if (key) form.append('file_keys', key);
  });
  const { headers } = await sessionGuarded(session, 'candidate_documents');
  await api.post(`${sessionBase(session)}/files`, form, {
    withCredentials: true,
    headers: { ...headers, 'Content-Type': 'multipart/form-data' },
  });
}

export async function getChecklist(code: string): Promise<Checklist> {
  const { data } = await api.get<Checklist>(`${base(code)}/checklist`);
  return data;
}

export async function submitChecklist(
  code: string,
  answers: { question_id: number; answer: string }[],
): Promise<ChecklistSubmitResult> {
  const { data } = await api.post<ChecklistSubmitResult>(
    `${base(code)}/checklist`,
    { answers },
    await guarded('candidate_checklist'),
  );
  return data;
}

export async function getCertifications(code: string): Promise<Certifications> {
  const { data } = await api.get<Certifications>(`${base(code)}/certifications`);
  return data;
}

export async function acceptCertificationsConsent(
  code: string,
  version: string,
): Promise<Certifications> {
  const { data } = await api.post<Certifications>(
    `${base(code)}/certifications/consent`,
    { version },
    await guarded('candidate_certifications'),
  );
  return data;
}

export async function uploadCertifications(
  code: string,
  files: File[],
): Promise<Certifications> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { headers } = await guarded('candidate_certifications');
  const { data } = await api.post<Certifications>(`${base(code)}/certifications`, form, {
    headers: { ...headers, 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function getLink(code: string): Promise<CandidateLink> {
  const { data } = await api.get<CandidateLink>(base(code));
  return data;
}

export async function acceptConsent(
  code: string,
  version: string,
): Promise<CandidateLink> {
  const { data } = await api.post<CandidateLink>(
    `${base(code)}/consent`,
    { version },
    await guarded('candidate_documents'),
  );
  return data;
}

export async function uploadFiles(
  code: string,
  files: File[],
  key?: string,
): Promise<CandidateLink> {
  const form = new FormData();
  files.forEach((file) => {
    form.append('files', file);
    if (key) form.append('file_keys', key);
  });
  const { headers } = await guarded('candidate_documents');
  const { data } = await api.post<CandidateLink>(`${base(code)}/files`, form, {
    headers: { ...headers, 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/**
 * Ask for a new link. The reply goes out on the channel already on file, so this
 * takes nothing but the spent code and never sees a phone number.
 */
export async function requestNewLink(code: string): Promise<ReissueStatus> {
  const { data } = await api.post<{ status: ReissueStatus }>(`${base(code)}/reissue`);
  return data.status;
}
